import { Box, Button, Modal, TextField } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { useAuth } from "../../hooks/useAuth";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import CustomTooltip from "../CustomTooltip";
import CnpjInput from "../mask/CnpjInput";
import PhoneInput from "../mask/PhoneInput";
import {
  FilesSectionSelector,
  FirstSectionCompany,
  StatusMyNotify
} from "../style/CompanyCreateModal.styled";
import StatusSwitch from "../switch/StatusSwitch";
import PageToaster from "../toaster/PageToaster";

interface CompanyCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCompanyCreated: () => void;
  company?: any;
}

const CompanyCreateModal: React.FC<CompanyCreateModalProps> = ({
  open,
  onClose,
  onCompanyCreated,
  company
}) => {
  const [companyName, setCompanyName] = useState("");
  const [companyCnpj, setCompanyCnpj] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [address, setAddress] = useState({
    address_city: "",
    address_state: "",
    address_street: "",
    address_neighborhood: "",
    address_number: "",
    address_zipcod: ""
  });

  const [parametrization, setParametrization] = useState({
    have_work_order: false,
    have_sale: false,
    have_tools: false,
    have_client: false,
    have_scheduling: false
  });

  const [selectedLogo, setSelectedLogo] = useState<File | null>(null);
  const [representativeLogos, setRepresentativeLogos] = useState<File[]>([]);

  const [isUpdate, setIsUpdate] = useState(false);
  const [isFetchingAddress, setIsFetchingAddress] = useState(false);

  const [haveWorkOrder, setHaveWorkOrder] = useState(false);
  const [haveSale, setHaveSale] = useState(false);
  const [haveTools, setHaveTools] = useState(false);
  const [haveClient, setHaveClient] = useState(false);
  const [haveScheduling, setHaveScheduling] = useState(false);

  const { isAdmin, isUser } = useAuth();

  const containerId = "toaster-company-create-modal";
  const { autoSuccess, autoError, autoInfo, autoWarn } = usePageToast(
    containerId,
    true
  );

  const handleHttpError = makeToasterErrorHandler(autoError);

  useEffect(() => {
    if (company) {
      console.log(">>>>>>>", company);

      setCompanyName(company.company_name || "");
      setCompanyCnpj(company.company_cnpj || "");
      setCompanyEmail(company.company_email || "");
      setPhoneNumber(company.phone?.phone_number || "");

      if (company.address) {
        setAddress({
          address_city: company.address.address_city || "",
          address_state: company.address.address_state || "",
          address_street: company.address.address_street || "",
          address_neighborhood: company.address.address_neighborhood || "",
          address_number: company.address.address_number || "",
          address_zipcod: company.address.address_zipcod || ""
        });
      }

      if (company.parametrization) {
        setParametrization({
          have_work_order: company.parametrization.have_workorder || false,
          have_sale: company.parametrization.have_sale || false,
          have_tools: company.parametrization.have_tools || false,
          have_client: company.parametrization.have_client || false,
          have_scheduling: company.parametrization.have_scheduler || false
        });
      }

      setHaveWorkOrder(company.parametrization?.have_workorder || false);
      setHaveSale(company.parametrization?.have_sale || false);
      setHaveTools(company.parametrization?.have_tools || false);
      setHaveClient(company.parametrization?.have_client || false);
      setHaveScheduling(company.parametrization?.have_scheduler || false);

      setIsUpdate(true);
    } else {
      clear();
    }
  }, [company]);

  const numberRef = useRef<HTMLInputElement>(null);

  const clear = () => {
    setCompanyName("");
    setCompanyCnpj("");
    setPhoneNumber("");
    setCompanyEmail("");
    setAddress({
      address_city: "",
      address_state: "",
      address_street: "",
      address_neighborhood: "",
      address_number: "",
      address_zipcod: ""
    });
    setParametrization({
      have_work_order: false,
      have_sale: false,
      have_tools: false,
      have_client: false,
      have_scheduling: false
    });
    setIsUpdate(false);
  };

  const handleCepBlur = useCallback(async () => {
    const cep = address.address_zipcod.replace(/\D/g, "");
    console.log("blur CEP:", cep);
    if (cep.length !== 8) return;

    setIsFetchingAddress(true);
    try {
      const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`);
      const data = await res.json();
      console.log("ViaCEP retornou:", data);
      if (data.erro) throw new Error("CEP não encontrado");

      setAddress((prev) => ({
        ...prev,
        address_street: data.street || "",
        address_neighborhood: data.neighborhood || "",
        address_city: data.city || "",
        address_state: data.state || ""
      }));
      // após setar, joga foco para o campo número
      setTimeout(() => numberRef.current?.focus(), 100);
    } catch (error) {
      handleHttpError(error);
    } finally {
      setIsFetchingAddress(false);
    }
  }, [address.address_zipcod]);

  const handleSubmit = async () => {
    try {
      const formData = new FormData();
      formData.append("company_name", companyName);
      formData.append("company_cnpj", companyCnpj);
      formData.append("company_email", companyEmail);
      formData.append("phone", JSON.stringify({ phone_number: phoneNumber }));
      formData.append("address", JSON.stringify(address));

      formData.append(
        "parametrization",
        JSON.stringify({
          have_workOrder: haveWorkOrder,
          have_sale: haveSale,
          have_tools: haveTools,
          have_client: haveClient,
          have_scheduler: haveScheduling
        })
      );

      if (selectedLogo) {
        formData.append("logo_principal", selectedLogo);
      }

      representativeLogos.forEach((file) =>
        formData.append("representative_logos", file)
      );

      if (isUpdate) {
        await api.put(`/company/${company.company_id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });
        autoSuccess("Empresa atualizada com sucesso!");
      } else {
        await api.post("/company", formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });
        autoSuccess("Empresa cadastrada com sucesso!");
      }

      onCompanyCreated();
      clear();
      onClose();
    } catch (error) {
      console.error("Erro ao salvar empresa:", error);
      handleHttpError(error);
    }
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="create-company-modal"
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "var(--surface)",
            border: "1px solid var(--border-strong)",
            color: "var(--text)",
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            width: "80vw",
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <h2>{isUpdate ? "Editar Empresa" : "Cadastrar Empresa"}</h2>

          <FirstSectionCompany>
            <TextField
              style={{ width: "80%" }}
              fullWidth
              label="Nome da Empresa"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              margin="normal"
            />
            {/* <TextField style={{ width: '700px', textAlign: 'center' }} fullWidth label="CNPJ" value={companyCnpj} onChange={(e) => setCompanyCnpj(e.target.value)} margin="normal" /> */}

            <CnpjInput
              value={companyCnpj}
              onChange={setCompanyCnpj}
              label="CNPJ"
              sx={{ mb: 2, width: "55%" }}
              margin="normal"
            />
            {/* <TextField style={{ width: '700px' }} fullWidth label="Telefone" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} margin="normal" /> */}
            <PhoneInput
              value={phoneNumber}
              onChange={(value) => setPhoneNumber(value)}
              label="Telefone"
              sx={{ width: "55%" }}
              margin="normal"
            />
            <TextField
              fullWidth
              label="E-mail"
              value={companyEmail}
              onChange={(e) => setCompanyEmail(e.target.value)}
              margin="normal"
            />
          </FirstSectionCompany>

          <FirstSectionCompany>
            <FilesSectionSelector>
              <label htmlFor="">Ordem de Serviço</label>
              {isAdmin && (
                <td>
                  {" "}
                  <StatusSwitch
                    checked={haveWorkOrder}
                    onToggle={() => {
                      if (!haveWorkOrder) {
                        autoSuccess(
                          "Ordem de serviço ativada, todos os usuários terão acesso a essa funcionalidade."
                        );
                      } else {
                        autoWarn(
                          "Ordem de serviço desativada, nenhum usuário terá acesso a essa funcionalidade."
                        );
                      }
                      setHaveWorkOrder(!haveWorkOrder);
                    }}
                  />
                </td>
              )}
              {haveWorkOrder ? (
                <StatusMyNotify status={haveWorkOrder}>Ativo</StatusMyNotify>
              ) : (
                <StatusMyNotify status={haveWorkOrder}>Inativo</StatusMyNotify>
              )}
            </FilesSectionSelector>

            <FilesSectionSelector>
              <label htmlFor="">Vendas</label>
              {isAdmin && (
                <td>
                  {" "}
                  <StatusSwitch
                    checked={haveSale}
                    onToggle={() => {
                      if (!haveSale) {
                        autoSuccess(
                          "Módulo de vendas ativado, todos os usuários terão acesso a essa funcionalidade."
                        );
                      } else {
                        autoWarn(
                          "Módulo de vendas desativado, nenhum usuário terá acesso a essa funcionalidade."
                        );
                      }
                      setHaveSale(!haveSale);
                    }}
                  />
                </td>
              )}
              {haveSale ? (
                <StatusMyNotify status={haveSale}>Ativo</StatusMyNotify>
              ) : (
                <StatusMyNotify status={haveSale}>Inativo</StatusMyNotify>
              )}
            </FilesSectionSelector>

            <FilesSectionSelector>
              <label htmlFor="">Ativos</label>
              {isAdmin && (
                <td>
                  {" "}
                  <StatusSwitch
                    checked={haveTools}
                    onToggle={() => {
                      if (!haveTools) {
                        autoSuccess(
                          "Módulo de ferramentas ativado, todos os usuários terão acesso a essa funcionalidade."
                        );
                      } else {
                        autoWarn(
                          "Módulo de ferramentas desativado, nenhum usuário terá acesso a essa funcionalidade."
                        );
                      }
                      setHaveTools(!haveTools);
                    }}
                  />
                </td>
              )}
              {haveTools ? (
                <StatusMyNotify status={haveTools}>Ativo</StatusMyNotify>
              ) : (
                <StatusMyNotify status={haveTools}>Inativo</StatusMyNotify>
              )}
            </FilesSectionSelector>

            <FilesSectionSelector>
              <label htmlFor="">Cliente</label>
              {isAdmin && (
                <td>
                  {" "}
                  <StatusSwitch
                    checked={haveClient}
                    onToggle={() => {
                      if (!haveClient) {
                        autoSuccess(
                          "Módulo de clientes ativado, todos os usuários terão acesso a essa funcionalidade."
                        );
                      } else {
                        autoWarn(
                          "Módulo de clientes desativado, nenhum usuário terá acesso a essa funcionalidade."
                        );
                      }
                      setHaveClient(!haveClient);
                    }}
                  />
                </td>
              )}
              {haveClient ? (
                <StatusMyNotify status={haveClient}>Ativo</StatusMyNotify>
              ) : (
                <StatusMyNotify status={haveClient}>Inativo</StatusMyNotify>
              )}
            </FilesSectionSelector>

            <FilesSectionSelector>
              <label htmlFor="">Agendamento</label>
              {isAdmin && (
                <td>
                  {" "}
                  <StatusSwitch
                    checked={haveScheduling}
                    onToggle={() => {
                      if (!haveScheduling) {
                        autoSuccess(
                          "Módulo de agendamento ativado, todos os usuários terão acesso a essa funcionalidade."
                        );
                      } else {
                        autoWarn(
                          "Módulo de agendamento desativado, nenhum usuário terá acesso a essa funcionalidade."
                        );
                      }
                      setHaveScheduling(!haveScheduling);
                    }}
                  />
                </td>
              )}
              {haveScheduling ? (
                <StatusMyNotify status={haveScheduling}>Ativo</StatusMyNotify>
              ) : (
                <StatusMyNotify status={haveScheduling}>Inativo</StatusMyNotify>
              )}
            </FilesSectionSelector>
          </FirstSectionCompany>

          <h2>Endereço</h2>
          <FirstSectionCompany>
            <TextField
              fullWidth
              label="CEP"
              value={address.address_zipcod}
              onChange={(e) =>
                setAddress({ ...address, address_zipcod: e.target.value })
              }
              margin="normal"
            />

            <CustomTooltip title="Buscar CEP">
              <button
                style={{
                  width: "150px",
                  height: "55px",
                  backgroundColor: "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  marginTop: "16px",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)"
                }}
                onClick={handleCepBlur}
              >
                <FaSearch />
              </button>
            </CustomTooltip>

            <TextField
              fullWidth
              label="Cidade"
              value={address.address_city}
              onChange={(e) =>
                setAddress({ ...address, address_city: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Estado"
              value={address.address_state}
              onChange={(e) =>
                setAddress({ ...address, address_state: e.target.value })
              }
              margin="normal"
            />
          </FirstSectionCompany>

          <FirstSectionCompany>
            <TextField
              fullWidth
              label="Rua"
              value={address.address_street}
              onChange={(e) =>
                setAddress({ ...address, address_street: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Bairro"
              value={address.address_neighborhood}
              onChange={(e) =>
                setAddress({ ...address, address_neighborhood: e.target.value })
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Número"
              value={address.address_number}
              onChange={(e) =>
                setAddress({ ...address, address_number: e.target.value })
              }
              margin="normal"
              inputRef={numberRef}
            />
          </FirstSectionCompany>

          <Box sx={{ mt: 2, display: "flex", flexDirection: "row", gap: 5 }}>
            <Button component="label" variant="outlined" fullWidth>
              Logo Principal
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) =>
                  e.target.files && setSelectedLogo(e.target.files[0])
                }
              />
            </Button>

            <Button component="label" variant="outlined" fullWidth>
              Logos de Representantes
              <input
                type="file"
                accept="image/*"
                hidden
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (files.length > 6) {
                    autoError("Só pode carregar no maximo 6 amagens.");
                    setRepresentativeLogos([]);
                    return;
                  }

                  setRepresentativeLogos(files);
                }}
              />
            </Button>
          </Box>

          <div style={{ marginTop: "20px", textAlign: "right" }}>
            <Button
              onClick={onClose}
              style={{ marginRight: "10px" }}
              color="secondary"
            >
              Cancelar
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained">
              {isUpdate ? "Atualizar" : "Cadastrar"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CompanyCreateModal;
